'use client';

import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import Link, { type LinkProps } from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { scrollToAnchor } from '@/utils/scrollToAnchor';

// Toggle this to test transitions in development
const ENABLE_TRANSITIONS_IN_DEV = false;

const ANIMATION_DURATION = 500;
const NAVIGATION_TIMEOUT = 2000;

// --- Context ---

interface TransitionContextValue {
  startTransition: (path: string, hash: string | null) => void;
  isNavigatingRef: React.MutableRefObject<boolean>;
}

const TransitionContext = createContext<TransitionContextValue | null>(null);

// Stable no-op ref shared across all fallback consumers so identity is stable for useEffect deps.
const NOOP_NAVIGATING_REF: React.MutableRefObject<boolean> = { current: false };

type UsePageTransitionResult = TransitionContextValue & { isFallback: boolean };

/**
 * Returns transition controls. If called outside <TransitionProvider> (a layout
 * bug), returns a fallback that lets consumers degrade to default <Link> behavior
 * instead of crashing the page. Logs in non-production so the bug is visible.
 */
function usePageTransition(): UsePageTransitionResult {
  const ctx = useContext(TransitionContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
      console.warn(
        '[TransitionLink] usePageTransition called outside <TransitionProvider> — ' +
          'falling back to default <Link> behavior. Wrap the consumer in <TransitionProvider>.'
      );
    }
    return {
      startTransition: () => {},
      isNavigatingRef: NOOP_NAVIGATING_REF,
      isFallback: true,
    };
  }
  return { ...ctx, isFallback: false };
}

// --- Force cleanup (class-based, no layout thrashing) ---

function forceCleanup(el: HTMLElement) {
  el.classList.remove('exit-animation', 'enter-animation');
  el.classList.add('transition-reset');
  requestAnimationFrame(() => el.classList.remove('transition-reset'));
}

// --- Provider (owns ALL transition state and pathname watching) ---

export function TransitionProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const isNavigatingRef = useRef(false);
  const targetPathRef = useRef('');
  const targetHashRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef(false);
  const pathname = usePathname();

  const completeNavigation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isNavigatingRef.current = false;
    targetPathRef.current = '';
    setIsLoading(false);
  }, []);

  const handleEnterAnimation = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    const el = document.getElementById('page-content');
    if (!el) {
      completeNavigation();
      return;
    }

    setIsLoading(false);
    el.classList.remove('exit-animation');
    el.classList.add('enter-animation');

    const savedHash = targetHashRef.current;

    setTimeout(() => {
      el.classList.remove('enter-animation');
      completeNavigation();
      hasCompletedRef.current = false;

      if (savedHash) {
        setTimeout(() => {
          scrollToAnchor(savedHash, { behavior: 'smooth' });
          targetHashRef.current = null;
        }, 50);
      }
    }, ANIMATION_DURATION);
  }, [completeNavigation]);

  // Watch for pathname changes to detect navigation completion
  // This runs in the Provider (never unmounts), so it always fires
  useEffect(() => {
    if (isNavigatingRef.current && pathname === targetPathRef.current) {
      const timer = setTimeout(handleEnterAnimation, 50);
      return () => clearTimeout(timer);
    }
  }, [pathname, handleEnterAnimation]);

  const startTransition = useCallback(
    (path: string, hash: string | null) => {
      isNavigatingRef.current = true;
      targetPathRef.current = path;
      targetHashRef.current = hash;
      hasCompletedRef.current = false;

      // Fallback timeout in case navigation stalls
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        console.warn('Navigation timeout — forcing transition completion');
        handleEnterAnimation();
      }, ANIMATION_DURATION + NAVIGATION_TIMEOUT);
    },
    [handleEnterAnimation]
  );

  return (
    <TransitionContext.Provider value={{ startTransition, isNavigatingRef }}>
      {children}
      {isLoading && (
        <div className="transition-loader-overlay">
          <div className="transition-loader-icon">
            <img src="/mark.svg" alt="" width={70} height={70} />
          </div>
        </div>
      )}
    </TransitionContext.Provider>
  );
}

// --- Loader (Safari/visibility edge cases only) ---

export function TransitionLoader() {
  const { isNavigatingRef } = usePageTransition();

  // Safari bfcache recovery
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        const el = document.getElementById('page-content');
        if (el) forceCleanup(el);
        isNavigatingRef.current = false;
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isNavigatingRef]);

  // Clean stuck animations on tab re-focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const el = document.getElementById('page-content');
        if (el && (el.classList.contains('exit-animation') || el.classList.contains('enter-animation'))) {
          forceCleanup(el);
          isNavigatingRef.current = false;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isNavigatingRef]);

  // Initial cleanup on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById('page-content');
      if (el && (el.classList.contains('exit-animation') || el.classList.contains('enter-animation'))) {
        forceCleanup(el);
        isNavigatingRef.current = false;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isNavigatingRef]);

  return null;
}

// --- TransitionLink ---

interface TransitionLinkProps extends Omit<LinkProps, 'onClick'> {
  href: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  'data-menu'?: string;
  'data-location'?: string;
  'aria-label'?: string;
   
  onClick?: (...args: any[]) => void;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  ref?: React.Ref<HTMLAnchorElement>;
  id?: string;
  disabled?: boolean;
  tabIndex?: number;
  'aria-hidden'?: boolean;
}

export default function TransitionLink({
  href,
  children,
  onClick,
  ...props
}: TransitionLinkProps) {
  const router = useRouter();
  const { startTransition, isNavigatingRef, isFallback } = usePageTransition();

  async function handleNavigation(e: React.MouseEvent<HTMLAnchorElement>) {
    // No <TransitionProvider> above us — let the underlying <Link> handle navigation
    // natively. Don't preventDefault; native click behavior is the right fallback.
    if (isFallback) {
      onClick?.(e);
      return;
    }

    // Skip transitions in dev unless explicitly enabled
    if (process.env.NODE_ENV === 'development' && !ENABLE_TRANSITIONS_IN_DEV) {
      onClick?.(e);
      return;
    }

    e.preventDefault();
    onClick?.(e);

    if (isNavigatingRef.current) return;

    // Parse hash
    const hashIndex = href.indexOf('#');
    const hash = hashIndex !== -1 ? href.slice(hashIndex + 1) : null;
    const pathWithoutHash = hashIndex !== -1 ? href.slice(0, hashIndex) : href;

    // Same-page anchor link — just scroll, no transition
    const currentPath = window.location.pathname;
    if (hash && (!pathWithoutHash || pathWithoutHash === currentPath)) {
      scrollToAnchor(hash, { behavior: 'smooth' });
      return;
    }

    const el = document.getElementById('page-content');
    if (!el) return;

    // Tell the provider to start watching for the pathname change
    startTransition(pathWithoutHash || '/', hash);

    // Exit animation
    el.classList.add('exit-animation');
    await new Promise((r) => setTimeout(r, ANIMATION_DURATION));

    // Scroll to top instantly while hidden
    const html = document.documentElement;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = '';

    // Navigate
    try {
      router.push(href);
    } catch (error) {
      console.error('Navigation error:', error);
      const pc = document.getElementById('page-content');
      if (pc) forceCleanup(pc);
      isNavigatingRef.current = false;
    }
  }

  return (
    <Link onClick={handleNavigation} href={href} {...props}>
      {children}
    </Link>
  );
}
